import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PartnerProfile {
  id: string;
  user_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  partner_type: 'Affiliate' | 'Associate' | 'Reseller';
  status: 'Signed' | 'Not-Signed' | 'Informal';
  profile_image?: string;
  commission_rate?: number;
  total_earned?: number;
  deals_won?: number;
  revenue_generated?: number;
  partner_tier?: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  territory?: string;
  specialization?: string;
  notes?: string;
  conversion_rate?: number;
  goal_deals?: number;
  goal_revenue?: number;
}

export const usePartnerProfile = (userId?: string) => {
  return useQuery({
    queryKey: ["partner-profile", userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from('crm_partners')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching partner profile:", error);
        throw error;
      }
      
      return data as PartnerProfile | null;
    },
    enabled: !!userId
  });
};

export const useUpdatePartnerProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      partnerId, 
      profileData, 
      partnerData 
    }: { 
      partnerId: string; 
      profileData: any; 
      partnerData: Partial<PartnerProfile> 
    }) => {
      // Update profiles table
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error: profileError } = await supabase
        .from("profiles")
        .update(profileData)
        .eq("user_id", user.id);

      if (profileError) throw profileError;

      // Update crm_partners table
      const { error: partnerError } = await supabase
        .from('crm_partners')
        .update(partnerData)
        .eq('id', partnerId);

      if (partnerError) throw partnerError;

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-profile"] });
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update profile: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};